import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { EngineIcon, engineLabels } from 'Utils';
import { STATUS, FONT_MONO } from 'Utils/styleTokens';

/**
 * Shared "a job is running" table row. Used by the Data Discovery, Schema Metadata, and Secure
 * Data Landscape ledgers so the running state looks identical everywhere. While a source has an
 * in-flight job, its normal row is replaced by this one: name · engine · "<job type> running…"
 * (spanning the middle columns) · status · Cancel.
 *
 * @param leading    optional leading cell (the ledgers that lead with a date column pass it),
 *                   rendered before the name and accounted for in the middle cell's colSpan
 * @param name       data-source name (used when `nameNode` is not given)
 * @param nameNode   optional React node for the name cell (e.g. a link)
 * @param engine     engine/type key for the EngineIcon + label
 * @param jobType    human label of the running job ("Discovery", "Meta scan", "Mask", …)
 * @param status     status text for the pill (default "RUNNING")
 * @param totalCols  total column count of the host table, so the middle cell spans the remainder
 * @param completed  work items completed so far (progress bar shows only when total > 0)
 * @param total      total work items for the run
 * @param unit       optional unit label for the fraction ("tables", "files", "records")
 * @param resultsTo  optional route to this run's results. Given one, the row offers "View
 *                   results →" beside Cancel: a long run reports partial findings while it
 *                   works, and the ledger row used to offer nothing but cancelling, so the
 *                   only way to see them was to stop the run (C2D-1200). Omit it for a queued
 *                   job — there is nothing to show yet.
 * @param onCancel   cancel handler
 * @param cancelling disables the button and shows "Cancelling…"
 * @param testId     data-testid for the row (button gets `${testId}-cancel`, bar `${testId}-progress`)
 */
function RunningJobRow({
  leading, name, nameNode, engine, jobType, status, totalCols = 5, completed, total, unit, onCancel, cancelling, testId,
  resultsTo,
}) {
  // Fixed cells: [leading?] name · engine · middle · status · cancel. The middle cell soaks up
  // whatever the host table has left, so a ledger can add columns without touching this row.
  const middleSpan = Math.max(1, totalCols - (leading ? 5 : 4));
  const totalNum = Number(total);
  const completedNum = Number(completed);
  const hasProgress = Number.isFinite(totalNum) && totalNum > 0 && Number.isFinite(completedNum) && completedNum >= 0;
  const pct = hasProgress ? Math.min(100, Math.round((completedNum / totalNum) * 100)) : 0;
  return (
    <tr data-testid={testId} style={{ borderTop: '1px solid var(--ink-100)', background: STATUS.info.bg }}>
      {leading && <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>{leading}</td>}
      <td style={{ padding: '9px 10px', fontFamily: FONT_MONO, color: 'var(--ink-900)', fontWeight: 600 }}>
        {nameNode || name || '—'}
      </td>
      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
        {engine ? (
          <span>
            <EngineIcon engine={engine} /> {engineLabels[engine] || engine}
          </span>
        ) : (
          <span style={{ color: 'var(--ink-400)' }}>—</span>
        )}
      </td>
      <td colSpan={middleSpan} style={{ padding: '9px 10px' }}>
        <span className='d-inline-flex align-items-center' style={{ gap: 8, color: STATUS.info.text }}>
          <span
            className='running-dot'
            style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS.info.text, flex: 'none' }}
          />
          {jobType || 'Job'} running…
        </span>
        {hasProgress && (
          <span
            className='d-inline-flex align-items-center'
            style={{ gap: 8, marginLeft: 14 }}
            data-testid={testId ? `${testId}-progress` : undefined}
          >
            <span
              style={{
                width: 140,
                height: 6,
                borderRadius: 3,
                background: 'var(--ink-100)',
                overflow: 'hidden',
                flex: 'none',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '100%',
                  width: `${pct}%`,
                  background: STATUS.info.text,
                  transition: 'width .6s ease',
                }}
              />
            </span>
            <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
              {completedNum.toLocaleString()} of {totalNum.toLocaleString()}
              {unit ? ` ${unit}` : ''} · {pct}%
            </span>
          </span>
        )}
      </td>
      <td style={{ padding: '9px 10px' }}>
        <span className='sg-pill sg-pill--info' style={{ fontSize: 10.5 }}>
          {status || 'RUNNING'}
        </span>
      </td>
      <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
        {resultsTo && (
          <Link
            to={resultsTo}
            className='sg-small mr-3'
            data-testid={testId ? `${testId}-results` : undefined}
          >
            View results →
          </Link>
        )}
        <Button
          size='sm'
          variant='outline-danger'
          disabled={cancelling}
          onClick={onCancel}
          data-testid={testId ? `${testId}-cancel` : undefined}
        >
          {cancelling ? 'Cancelling…' : 'Cancel'}
        </Button>
      </td>
    </tr>
  );
}

export default RunningJobRow;
